import { HttpErrorResponse } from '@angular/common/http';

import { Language } from '@app/core/constants/language.constants';

export const COURSE_INTENTS = {
  KNOWLEDGE_TRANSFER: 'knowledge_transfer',
  COMPLIANCE_AWARENESS: 'awareness_compliance',
  SKILLS_BUILDING: 'skill_building',
  ONBOARDING: 'onboarding',
  CERTIFICATION_PREP: 'certification_prep',
  AI_DECIDE: 'unknown',
} as const;

export type CourseIntent = (typeof COURSE_INTENTS)[keyof typeof COURSE_INTENTS];

export const TARGET_AUDIENCES = {
  NEW_TO_TOPIC: 'new_to_topic',
  SOME_EXPERIENCE: 'some_experience',
  EXPERTS: 'experts',
} as const;

export type TargetAudience =
  (typeof TARGET_AUDIENCES)[keyof typeof TARGET_AUDIENCES];

export interface OptionConfig<T extends string = string> {
  readonly id: T;
  readonly titleKey: string;
  readonly descriptionKey: string;
  readonly icon: string;
}

export const COURSE_INTENT_OPTIONS: readonly OptionConfig<CourseIntent>[] = [
  {
    id: COURSE_INTENTS.KNOWLEDGE_TRANSFER,
    titleKey: 'guidedCourseCreation.intent.knowledgeTransfer.title',
    descriptionKey: 'guidedCourseCreation.intent.knowledgeTransfer.description',
    icon: 'auto_stories',
  },
  {
    id: COURSE_INTENTS.COMPLIANCE_AWARENESS,
    titleKey: 'guidedCourseCreation.intent.complianceAwareness.title',
    descriptionKey:
      'guidedCourseCreation.intent.complianceAwareness.description',
    icon: 'shield',
  },
  {
    id: COURSE_INTENTS.SKILLS_BUILDING,
    titleKey: 'guidedCourseCreation.intent.skillsBuilding.title',
    descriptionKey: 'guidedCourseCreation.intent.skillsBuilding.description',
    icon: 'build',
  },
  {
    id: COURSE_INTENTS.ONBOARDING,
    titleKey: 'guidedCourseCreation.intent.onboarding.title',
    descriptionKey: 'guidedCourseCreation.intent.onboarding.description',
    icon: 'person_add',
  },
  {
    id: COURSE_INTENTS.CERTIFICATION_PREP,
    titleKey: 'guidedCourseCreation.intent.certificationPrep.title',
    descriptionKey: 'guidedCourseCreation.intent.certificationPrep.description',
    icon: 'workspace_premium',
  },
  {
    id: COURSE_INTENTS.AI_DECIDE,
    titleKey: 'guidedCourseCreation.intent.aiDecide.title',
    descriptionKey: 'guidedCourseCreation.intent.aiDecide.description',
    icon: 'smart_toy',
  },
];

export const TARGET_AUDIENCE_OPTIONS: readonly OptionConfig<TargetAudience>[] =
  [
    {
      id: TARGET_AUDIENCES.NEW_TO_TOPIC,
      titleKey: 'guidedCourseCreation.audience.newToTopic.title',
      descriptionKey: 'guidedCourseCreation.audience.newToTopic.description',
      icon: 'face',
    },
    {
      id: TARGET_AUDIENCES.SOME_EXPERIENCE,
      titleKey: 'guidedCourseCreation.audience.someExperience.title',
      descriptionKey:
        'guidedCourseCreation.audience.someExperience.description',
      icon: 'hail',
    },
    {
      id: TARGET_AUDIENCES.EXPERTS,
      titleKey: 'guidedCourseCreation.audience.experts.title',
      descriptionKey: 'guidedCourseCreation.audience.experts.description',
      icon: 'work',
    },
  ];

export const COURSE_LENGTH_TYPES = {
  STANDARD: 'standard',
  LEARNING_NUGGET: 'learning_nugget',
} as const;

export type CourseLengthType =
  (typeof COURSE_LENGTH_TYPES)[keyof typeof COURSE_LENGTH_TYPES];

export const COURSE_LENGTH_TYPE_OPTIONS: readonly OptionConfig<CourseLengthType>[] =
  [
    {
      id: COURSE_LENGTH_TYPES.STANDARD,
      titleKey: 'guidedCourseCreation.lengthType.standard.title',
      descriptionKey: 'guidedCourseCreation.lengthType.standard.description',
      icon: 'update',
    },
    {
      id: COURSE_LENGTH_TYPES.LEARNING_NUGGET,
      titleKey: 'guidedCourseCreation.lengthType.learningNugget.title',
      descriptionKey:
        'guidedCourseCreation.lengthType.learningNugget.description',
      icon: 'bolt',
    },
  ];

interface ModuleTypeConfig {
  readonly displayKey: string;
  readonly iconType: string;
  readonly h5pTitle: string;
}

export type SuggestedModuleType =
  | 'interactive_video'
  | 'content'
  | 'checklist'
  | 'flashcards'
  | 'image_hotspots'
  | 'image_slider'
  | 'find_the_hotspots'
  | 'drag_the_words'
  | 'task'
  | 'quiz'
  | 'audio'
  | 'file_upload'
  | 'feedback_module'
  | 'link'
  | 'memory'
  | 'sort_the_paragraphs';

export const MODULE_TYPE_MAP: Record<SuggestedModuleType, ModuleTypeConfig> = {
  interactive_video: {
    displayKey: 'h5p.libraries.H5P.InteractiveVideo',
    iconType: 'Interactive video',
    h5pTitle: 'Interactive Video',
  },
  content: {
    displayKey: 'h5p.libraries.H5P.ContentPage',
    iconType: 'Content page',
    h5pTitle: 'Content',
  },
  checklist: {
    displayKey: 'h5p.libraries.H5P.Checklist',
    iconType: 'Checklist',
    h5pTitle: 'Checklist',
  },
  flashcards: {
    displayKey: 'h5p.libraries.H5P.Dialogcards',
    iconType: 'Flashcards',
    h5pTitle: 'Study Cards',
  },
  image_hotspots: {
    displayKey: 'h5p.libraries.H5P.ImageHotspots',
    iconType: 'Image hotspots',
    h5pTitle: 'Hotspots',
  },
  image_slider: {
    displayKey: 'h5p.libraries.H5P.ImageSlider',
    iconType: 'Image slider',
    h5pTitle: 'Image Slider',
  },
  find_the_hotspots: {
    displayKey: 'h5p.libraries.H5P.ImageHotspotQuestion',
    iconType: 'Image hotspot question',
    h5pTitle: 'Hotspot search',
  },
  drag_the_words: {
    displayKey: 'h5p.libraries.H5P.DragText',
    iconType: 'Drag text',
    h5pTitle: 'Drag the Words',
  },
  task: {
    displayKey: 'h5p.libraries.H5P.Task',
    iconType: 'Task',
    h5pTitle: 'Task',
  },
  quiz: {
    displayKey: 'h5p.libraries.H5P.QuestionSet',
    iconType: 'Question set',
    h5pTitle: 'Quiz',
  },
  audio: {
    displayKey: 'h5p.libraries.H5P.Audio',
    iconType: 'Audio',
    h5pTitle: 'Audio',
  },
  file_upload: {
    displayKey: 'h5p.libraries.H5P.File',
    iconType: 'File',
    h5pTitle: 'Slides',
  },
  feedback_module: {
    displayKey: 'h5p.libraries.H5P.Feedback',
    iconType: 'Feedback',
    h5pTitle: 'Feedback',
  },
  link: {
    displayKey: 'h5p.libraries.H5P.Link',
    iconType: 'Link',
    h5pTitle: 'Link',
  },
  memory: {
    displayKey: 'h5p.libraries.H5P.MemoryGame',
    iconType: 'Memory game',
    h5pTitle: 'Memory Game',
  },
  sort_the_paragraphs: {
    displayKey: 'h5p.libraries.H5P.SortParagraphs',
    iconType: 'Sort the Paragraphs',
    h5pTitle: 'Sort the Paragraphs',
  },
};

function isSuggestedModuleType(key: string): key is SuggestedModuleType {
  return key in MODULE_TYPE_MAP;
}

export function getModuleTypeConfig(
  key: string | undefined,
): ModuleTypeConfig | undefined {
  return key && isSuggestedModuleType(key) ? MODULE_TYPE_MAP[key] : undefined;
}

interface CourseModuleOutline {
  name: string;
  moduleType: string;
  rawModuleType?: SuggestedModuleType;
  estimatedTimeMinutes: number;
  whatToCover: string;
  iconPath?: string;
  moduleId?: number;
}

export interface CourseSectionOutline {
  headline: string;
  modules: CourseModuleOutline[];
}

export interface CourseOutline {
  title: string;
  totalTimeMinutes: number;
  learningObjectives: string[];
  sections: CourseSectionOutline[];
}

// Skeleton API types
export interface SkeletonRequestPayload {
  topic: string;
  language: Language;
  intent: CourseIntent;
  audience: TargetAudience[];
  length_type: CourseLengthType;
  document_mode?: DocumentMode;
}

export interface SkeletonSectionResponse {
  section_id: number;
  section_title: string;
  section_number: number;
  module_count: number;
}

export interface SkeletonResponse {
  course_title: string;
  estimated_duration_minutes: number;
  learning_objectives: string[];
  sections: SkeletonSectionResponse[];
  intent: CourseIntent;
  language: Language;
  audience: TargetAudience[];
}

// Session API types
export interface SessionResponse {
  data: { id: string };
}

export interface WrappedResponse<T> {
  data: T;
}

// Section content API types (per-section)
export interface SectionContentModule {
  module_id: number;
  type: SuggestedModuleType;
  title: string;
  what_to_cover: string;
  estimated_duration_minutes: number;
}

interface BulkCreateModule {
  module_number: number;
  type: string;
  title: string;
  what_to_cover: string;
  estimated_duration_minutes: number;
  content?: ModuleContentResponse;
}

interface BulkCreateSection {
  section_number: number;
  modules: BulkCreateModule[];
}

export interface BulkCreatePayload {
  language: string;
  sections: BulkCreateSection[];
}

export interface BulkCreateModuleResult {
  content_id: string;
  h5p_library: string;
  content_json: string;
  module_number: number;
  estimated_duration_minutes: number;
}

interface BulkCreateSectionResult {
  title: string;
  modules: BulkCreateModuleResult[];
}

export interface BulkCreateResponse {
  sections: BulkCreateSectionResult[];
}

export function buildBulkCreatePayload(
  outline: CourseOutline,
  language: string,
  contentMap?: Map<number, ModuleContentResponse>,
): BulkCreatePayload {
  return {
    language,
    sections: outline.sections.map((section, sectionIndex) => ({
      section_number: sectionIndex + 1,
      modules: section.modules.map((mod, modIndex) => {
        const module: BulkCreateModule = {
          module_number: modIndex + 1,
          type:
            getModuleTypeConfig(mod.rawModuleType)?.h5pTitle ?? mod.moduleType,
          title: mod.name,
          what_to_cover: mod.whatToCover,
          estimated_duration_minutes: mod.estimatedTimeMinutes,
        };
        if (contentMap && mod.moduleId && contentMap.has(mod.moduleId)) {
          module.content = contentMap.get(mod.moduleId);
        }
        return module;
      }),
    })),
  };
}

// Chat API types
export interface OutlineSectionApiResponse {
  section_id: number;
  section_title: string;
  section_number: number;
  modules: SectionContentModule[];
}

export interface OutlineApiResponse {
  course_title: string;
  estimated_duration_minutes: number;
  learning_objectives: string[];
  sections: OutlineSectionApiResponse[];
  intent: CourseIntent;
  language: Language;
  audience: TargetAudience[];
}

export interface ChatApiResponse {
  message: string;
  summary: string[];
  pending_actions: boolean;
  outline?: OutlineApiResponse;
}

export function formatLearningObjectives(
  objectives: readonly string[],
): string {
  const joined = objectives.join('. ');
  return joined ? `${joined}.` : '';
}

export function mapOutlineApiResponse(
  response: OutlineApiResponse,
): CourseOutline {
  return {
    title: response.course_title,
    totalTimeMinutes: response.estimated_duration_minutes,
    learningObjectives: response.learning_objectives ?? [],
    sections: response.sections.map(s => ({
      headline: s.section_title,
      modules: (s.modules ?? []).map(m => {
        const config = getModuleTypeConfig(m.type);
        return {
          name: m.title,
          moduleType: config?.displayKey ?? m.type,
          rawModuleType: m.type,
          estimatedTimeMinutes: m.estimated_duration_minutes,
          whatToCover: m.what_to_cover,
          moduleId: m.module_id,
        };
      }),
    })),
  };
}

// Chat message types
export const MESSAGE_TYPES = {
  USER: 'user',
  AI: 'ai',
  PREVIEW: 'preview',
} as const;

export type MessageType = (typeof MESSAGE_TYPES)[keyof typeof MESSAGE_TYPES];

export interface ChatMessage {
  type: MessageType;
  content: string;
  summary?: string[];
  pendingActions?: boolean;
  isError?: boolean;
}

export const AI_ERROR_CODES = {
  GENERIC: 'generic',
  TIMEOUT: 'timeout',
} as const;

export type AiErrorCode = (typeof AI_ERROR_CODES)[keyof typeof AI_ERROR_CODES];

const AI_ERROR_TRANSLATION_KEYS: Record<AiErrorCode, string> = {
  [AI_ERROR_CODES.GENERIC]: 'guidedCourseCreation.errorDialog.aiGenericError',
  [AI_ERROR_CODES.TIMEOUT]: 'guidedCourseCreation.errorDialog.aiTimeout',
};

export function getAiErrorTranslationKey(code: AiErrorCode): string {
  return AI_ERROR_TRANSLATION_KEYS[code];
}

export const CHAT_ERROR_KEYS = {
  TIMEOUT: 'guidedCourseCreation.chat.errorTimeout',
  GENERIC: 'guidedCourseCreation.chat.error',
} as const;

export function getChatErrorKey(status: number): string {
  return status === 504 ? CHAT_ERROR_KEYS.TIMEOUT : CHAT_ERROR_KEYS.GENERIC;
}

export const GCC_DOCUMENTS_PATH = '/documents';

// Source document upload (analysed to suggest course topics)
export const DOCUMENT_UPLOAD = {
  ACCEPTED_MIME_TYPES: ['application/pdf', 'text/plain', 'text/markdown'],
  ACCEPTED_EXTENSIONS: ['.pdf', '.txt', '.md'],
  ACCEPTED_FILE_TYPES: 'application/pdf,text/plain,text/markdown,.pdf,.txt,.md',
  MAX_BYTES: 32_000_000,
  MAX_SIZE_LABEL: '32 MB',
  MAX_DOCUMENTS: 3,
  MAX_SUGGESTED_TOPICS: 6,
} as const;

export const DOCUMENT_REJECTION_REASONS = {
  INVALID_TYPE: 'invalid_type',
  TOO_LARGE: 'too_large',
} as const;

export type DocumentRejectionReason =
  (typeof DOCUMENT_REJECTION_REASONS)[keyof typeof DOCUMENT_REJECTION_REASONS];

export const DOCUMENT_UPLOAD_STATES = {
  IDLE: 'idle',
  UPLOADING: 'uploading',
  DONE: 'done',
  ERROR: 'error',
} as const;

export type DocumentUploadState =
  (typeof DOCUMENT_UPLOAD_STATES)[keyof typeof DOCUMENT_UPLOAD_STATES];

export const DOCUMENT_MODES = {
  IGNORED: 'ignored',
  RELAXED: 'relaxed',
  STRICT: 'strict',
} as const;

export type DocumentMode = (typeof DOCUMENT_MODES)[keyof typeof DOCUMENT_MODES];

export const CONTENT_MODE_ORDER: readonly DocumentMode[] = [
  DOCUMENT_MODES.IGNORED,
  DOCUMENT_MODES.RELAXED,
  DOCUMENT_MODES.STRICT,
];

export interface DocumentUploadStatus {
  state: DocumentUploadState;
  suggestedTopics: readonly string[];
  documentMode: DocumentMode;
}

export const IDLE_DOCUMENT_STATUS: DocumentUploadStatus = {
  state: DOCUMENT_UPLOAD_STATES.IDLE,
  suggestedTopics: [],
  documentMode: DOCUMENT_MODES.RELAXED,
};

export interface DocumentAnalysis {
  suggested_topics?: string[];
  language?: string;
  document_type?: string;
  page_count?: number;
}

export interface DocumentUploadResponse {
  id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  analysis?: DocumentAnalysis;
}

interface MutationErrorItem {
  detail?: string;
}

interface MutationErrorBody {
  errors?: MutationErrorItem[];
}

const DOCUMENT_REJECTION_KEYS: Record<DocumentRejectionReason, string> = {
  [DOCUMENT_REJECTION_REASONS.INVALID_TYPE]:
    'guidedCourseCreation.document.rejected.invalidType',
  [DOCUMENT_REJECTION_REASONS.TOO_LARGE]:
    'guidedCourseCreation.document.rejected.tooLarge',
};

export const DOCUMENT_ERROR_KEYS = {
  TIMEOUT: 'guidedCourseCreation.document.error.timeout',
  GENERIC: 'guidedCourseCreation.document.error.generic',
  REMOVE_FAILED: 'guidedCourseCreation.document.error.removeFailed',
} as const;

// The backend's `detail` is the only stable discriminator: `code` is always
// "100" and the HTTP status is unreliable for AI failures.
const DOCUMENT_ERROR_DETAIL_KEYS: Record<string, string> = {
  'document|errors.i18n.invalid_type':
    'guidedCourseCreation.document.rejected.invalidType',
  'document|errors.i18n.size':
    'guidedCourseCreation.document.rejected.tooLarge',
  'document|errors.i18n.too_many_pages':
    'guidedCourseCreation.document.rejected.tooManyPages',
  'document|errors.i18n.unreadable':
    'guidedCourseCreation.document.rejected.unreadable',
  'document|errors.i18n.cant_be_blank':
    'guidedCourseCreation.document.rejected.invalidType',
  'upload|errors.i18n.too_many_documents':
    'guidedCourseCreation.document.rejected.tooManyFiles',
  'ai_timeout|errors.i18n.timeout': DOCUMENT_ERROR_KEYS.TIMEOUT,
};

// Lenient on type: browsers report markdown inconsistently, and the backend
// re-validates by sniffing the content anyway.
export function validateDocumentFile(
  file: File,
): DocumentRejectionReason | null {
  const name = file.name.toLowerCase();
  const isAccepted =
    (DOCUMENT_UPLOAD.ACCEPTED_MIME_TYPES as readonly string[]).includes(
      file.type,
    ) || DOCUMENT_UPLOAD.ACCEPTED_EXTENSIONS.some(ext => name.endsWith(ext));

  if (!isAccepted) {
    return DOCUMENT_REJECTION_REASONS.INVALID_TYPE;
  }

  return file.size > DOCUMENT_UPLOAD.MAX_BYTES
    ? DOCUMENT_REJECTION_REASONS.TOO_LARGE
    : null;
}

export function getDocumentRejectionKey(
  reason: DocumentRejectionReason,
): string {
  return DOCUMENT_REJECTION_KEYS[reason];
}

// Interleaved so every document is represented instead of the first one filling the row.
export function mergeSuggestedTopics(
  topicsPerDocument: readonly (readonly string[])[],
  limit: number = DOCUMENT_UPLOAD.MAX_SUGGESTED_TOPICS,
): readonly string[] {
  const depth = Math.max(0, ...topicsPerDocument.map(topics => topics.length));

  const interleaved = Array.from({ length: depth }).flatMap((_, round) =>
    topicsPerDocument
      .map(topics => topics[round]?.trim())
      .filter((topic): topic is string => !!topic),
  );

  return [...new Set(interleaved)].slice(0, limit);
}

export function getDocumentErrorKey(error: HttpErrorResponse): string {
  const body = error.error as MutationErrorBody | null;
  const matched = (body?.errors ?? [])
    .map(({ detail }) => detail && DOCUMENT_ERROR_DETAIL_KEYS[detail])
    .find(Boolean);

  return matched || DOCUMENT_ERROR_KEYS.GENERIC;
}

// AI content generation supported module types
export const AI_CONTENT_SUPPORTED_MODULES: ReadonlySet<SuggestedModuleType> =
  new Set<SuggestedModuleType>([
    'content',
    'flashcards',
    'quiz',
    'checklist',
    'task',
    'drag_the_words',
    'sort_the_paragraphs',
    'memory',
  ]);

export function isAiContentSupported(
  rawModuleType?: SuggestedModuleType,
): boolean {
  return !!rawModuleType && AI_CONTENT_SUPPORTED_MODULES.has(rawModuleType);
}

// Module types for which AI image generation is supported (subset of content-supported).
export const AI_IMAGE_SUPPORTED_MODULES: ReadonlySet<SuggestedModuleType> =
  new Set<SuggestedModuleType>(['flashcards', 'memory']);

export function isAiImageSupported(
  rawModuleType?: SuggestedModuleType,
): boolean {
  return !!rawModuleType && AI_IMAGE_SUPPORTED_MODULES.has(rawModuleType);
}

// Module content generation response types
export interface ContentModuleResponse {
  title: string;
  body: string;
}

export interface FlashcardsModuleResponse {
  cards: {
    front: string;
    back: string;
    image_description: string;
    image?: string;
  }[];
}

export interface QuizOptionResponse {
  text: string;
  correct: boolean;
}

export const QUIZ_QUESTION_TYPES = {
  SINGLE_CHOICE: 'single_choice',
  MULTIPLE_CHOICE: 'multiple_choice',
  TRUE_FALSE: 'true_false',
} as const;

export type QuizQuestionType =
  (typeof QUIZ_QUESTION_TYPES)[keyof typeof QUIZ_QUESTION_TYPES];

export interface QuizQuestionResponse {
  type: QuizQuestionType;
  text: string;
  options: QuizOptionResponse[];
}

export interface QuizModuleResponse {
  questions: QuizQuestionResponse[];
}

export interface ChecklistModuleResponse {
  description: string;
  items: string[];
}

export const TASK_SUBMISSION_TYPES = {
  TEXT: 'text',
  VIDEO: 'video',
  AUDIO: 'audio',
} as const;

export type TaskSubmissionType =
  (typeof TASK_SUBMISSION_TYPES)[keyof typeof TASK_SUBMISSION_TYPES];

export interface TaskModuleResponse {
  description: string;
  instructions: string;
  submission_type: TaskSubmissionType;
}

export interface DragTheWordsModuleResponse {
  description: string;
  text: string;
  distractors: string[];
}

export interface SortParagraphsModuleResponse {
  instructions: string;
  paragraphs: string[];
}

export interface MemoryModuleResponse {
  description: string;
  pairs: {
    text: string;
    match: string;
    image_description: string;
    image?: string;
  }[];
}

export type ModuleContentResponse =
  | ContentModuleResponse
  | FlashcardsModuleResponse
  | QuizModuleResponse
  | ChecklistModuleResponse
  | TaskModuleResponse
  | DragTheWordsModuleResponse
  | SortParagraphsModuleResponse
  | MemoryModuleResponse;
